import { useIntl } from "@components/Intl"
import {
  ComponentFilter,
  ProductGroupFilter,
  SizeFilter,
  StyleFilter,
} from "@globalTypes"
import { NextRouter, useRouter } from "next/router"
import qs from "query-string"
import { Dispatch, useEffect, useRef } from "react"
import { FilterAction, FilterState, initialState } from "./reducer"

export function useSearchUrlSync(
  { size, productGroup, style, query, initiated }: FilterState,
  dispatch: Dispatch<FilterAction>
) {
  const isSynced = useRef(false)
  const router = useRouter()
  const { locale } = useIntl()
  const mainHref = `/[lang]/${locale === "nl" ? "zoeken" : "search"}`
  const mainUrl = locale === "nl" ? "/nl/zoeken" : "/en/search"

  useEffect(() => {
    if (isSynced.current) {
      const params = qs.stringify(
        { size, productGroup, style, query },
        { arrayFormat: "comma", skipEmptyString: true, skipNull: true }
      )
      const href = `${mainHref}?${params}`
      const as = `${mainUrl}?${params}`
      router.replace(href, as, { shallow: true })
    }
  }, [size, productGroup, style, query]) // eslint-disable-line

  useEffect(() => {
    dispatchInitialState(dispatch, router.query)
    isSynced.current = true
  }, [initiated]) // eslint-disable-line

  // This effect runs on init to fire up an initial rerender to keep the url in sync with state
  useEffect(() => {
    const query = {
      size: router.query?.size || null,
      productGroup: router.query?.productGroup || null,
      style: router.query?.style || null,
      query: router.query?.query || "",
    }
    const href = `${mainHref}?${qs.stringify(query, {
      arrayFormat: "comma",
      skipEmptyString: true,
      skipNull: true,
    })}`
    const as = `${mainUrl}?${qs.stringify(query, {
      arrayFormat: "comma",
      skipEmptyString: true,
      skipNull: true,
    })}`
    dispatch({ type: "set-initial-value", value: query.query as string })

    router.replace(href, as, { shallow: true })
  }, []) // eslint-disable-line
}

function dispatchInitialState(
  dispatch: Dispatch<FilterAction>,
  query: NextRouter["query"]
) {
  let state = initialState

  if (query.size) {
    state.size = query?.size as string as SizeFilter
  }

  if (query.productGroup) {
    state.productGroup = query?.productGroup as string as ProductGroupFilter &
      Omit<ComponentFilter, "fixed">
  }

  if (query.style) {
    state.style = query?.style as string as StyleFilter
  }

  if (query.query) {
    state.query = query?.query as string
    state.initialValue = query?.query as string
  }
  dispatch({ type: "set-initial-state", state })
}
